import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Key, WarningCircle } from "@phosphor-icons/react";
import { portalApi } from "@/lib/portalApi";
import { formatApiError } from "@/lib/api";
import { usePortalAuth } from "@/contexts/PortalAuthContext";
import PortalAuthShell, { authCard, authLabel, authInput, authButton } from "./PortalAuthShell";

const MIN_LEN = 8;

/**
 * Handles BOTH flows, because they're the same action from the customer's side:
 *   kind "reset"    — forgot-password link
 *   kind "activate" — first-time link emailed when staff approve an access request
 *
 * The token is validated up front (token-info) so a dead link fails immediately
 * instead of after the user has typed a password. A successful reset returns a
 * session, so we drop straight into the portal rather than back to the login form.
 */
export default function PortalResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { adoptSession } = usePortalAuth();

  const [checking, setChecking] = useState(true);
  const [info, setInfo] = useState(null);     // {email, kind}
  const [deadReason, setDeadReason] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setDeadReason("This link is missing its token."); setChecking(false); return; }
    portalApi.get("/portal/auth/token-info", { params: { token } })
      .then(({ data }) => setInfo(data))
      .catch((e) => setDeadReason(formatApiError(e)))
      .finally(() => setChecking(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < MIN_LEN) { toast.error(`Password must be at least ${MIN_LEN} characters`); return; }
    if (pw !== pw2) { toast.error("The two passwords don't match"); return; }
    setBusy(true);
    try {
      const { data } = await portalApi.post("/portal/auth/reset-password", { token, password: pw });
      await adoptSession(data);
      toast.success("Password set — you're signed in.");
      navigate("/portal");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  if (checking) {
    return <PortalAuthShell title="One moment…" subtitle="Checking your link." backTo={null}><div className={authCard}><div className="text-sm text-slate-500">Verifying…</div></div></PortalAuthShell>;
  }

  if (deadReason) {
    return (
      <PortalAuthShell title="This link can't be used" subtitle="Reset links are single-use and expire.">
        <div className={authCard} data-testid="portal-reset-dead">
          <div className="flex items-start gap-3">
            <WarningCircle size={20} weight="fill" className="text-amber-500 mt-0.5 shrink-0" />
            <div className="text-sm text-slate-600">
              <p>{deadReason}</p>
              <p className="mt-2">Request a fresh link and it'll arrive in a moment.</p>
            </div>
          </div>
          <Link to="/portal/forgot-password" className={authButton} data-testid="portal-reset-request-new">
            Request a new link
          </Link>
        </div>
      </PortalAuthShell>
    );
  }

  const activating = info?.kind === "activate";
  return (
    <PortalAuthShell
      title={activating ? "Welcome — set your password" : "Set a new password"}
      subtitle={info?.email ? `For ${info.email}` : undefined}
    >
      <form onSubmit={submit} className={authCard}>
        <div>
          <label className={authLabel}>New password</label>
          <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} required autoFocus
            data-testid="portal-reset-pw" className={authInput} placeholder="••••••••" />
          <p className="text-[11px] text-slate-500 mt-1">At least {MIN_LEN} characters.</p>
        </div>
        <div>
          <label className={authLabel}>Confirm password</label>
          <input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} required
            data-testid="portal-reset-pw2" className={authInput} placeholder="••••••••" />
        </div>
        <button type="submit" disabled={busy} data-testid="portal-reset-btn" className={authButton}>
          <Key size={16} /> {busy ? "Saving…" : (activating ? "Set password and sign in" : "Save new password")}
        </button>
      </form>
    </PortalAuthShell>
  );
}
